// billingApi.ts — typed wrappers for PlatformBillingService (Phase 3a).
// All calls go through adminCall which handles auth headers + 401s.

import { adminCall } from './api';
import { num } from '../api/num';

// ─── Platform Stripe config ───

export interface PlatformStripeConfigSummary {
  configured: boolean;
  active: boolean;
  mode: string;
  publishableKey: string;
  accountId: string;
  statementDescriptor: string;
  lastVerifiedAt?: string;
  verifyError?: string;
  connectedAt?: string;
}

// ─── Plans ───

export interface Plan {
  id: string;
  version: number;
  slug: string;
  name: string;
  description: string;
  currency: string;
  status: string;
  billingInterval: string;
  recurringFeeCents: number | string;
  trialDays: number;
  commissionBps: number;
  storageTierModelJson: string;
  includedTransactionsCents: number | string;
  transactionOverageBps: number;
  lateFeeRateBps: number;
  lateFeeFlatCents: number | string;
  dunningWarningAtDays: number;
  dunningRestrictAtDays: number;
  dunningSuspendAtDays: number;
  dunningOffboardNoticeDays: number;
  dunningOffboardEligibleDays: number;
  paymentTermsDays: number;
  featuresJson: string;
  metadataJson: string;
  createdAt: string;
  supersededAt?: string;
}

export interface CreatePlanInput {
  slug: string;
  name: string;
  description?: string;
  currency?: string;
  billingInterval?: string;
  recurringFeeCents?: number;
  trialDays?: number;
  commissionBps?: number;
  storageTierModelJson?: string;
  includedTransactionsCents?: number;
  transactionOverageBps?: number;
  lateFeeRateBps?: number;
  lateFeeFlatCents?: number;
  dunningWarningAtDays?: number;
  dunningRestrictAtDays?: number;
  dunningSuspendAtDays?: number;
  dunningOffboardNoticeDays?: number;
  dunningOffboardEligibleDays?: number;
  paymentTermsDays?: number;
  featuresJson?: string;
  metadataJson?: string;
  activate?: boolean;
}

// ─── Subscriptions ───

export interface Subscription {
  id: string;
  orgId: string;
  planId: string;
  planSlug: string;
  planVersion: number;
  status: string;
  complimentary: boolean;
  complimentaryReason?: string;
  trialEndsAt?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  platformStripeCustomerId?: string;
  paymentMethodState: string;
  billingEmail?: string;
  dunningState: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── Invoices ───

export interface InvoiceItem {
  ordinal: number;
  kind: string;
  description: string;
  quantity: number;
  unit: string;
  unitCents: number | string;
  amountCents: number | string;
}

export interface Invoice {
  id: string;
  orgId: string;
  number: string;
  billingPeriod: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  currency: string;
  subtotalCents: number | string;
  taxCents: number | string;
  creditAppliedCents: number | string;
  totalCents: number | string;
  amountPaidCents: number | string;
  amountRefundedCents: number | string;
  issuedAt?: string;
  dueAt?: string;
  finalizedAt?: string;
  paidAt?: string;
  voidedAt?: string;
  writtenOffAt?: string;
  pdfStorageKey?: string;
  pdfRenderedAt?: string;
  items?: InvoiceItem[];
}

// ─── RPC wrappers ───

export const platformBilling = {
  // Stripe config
  getStripeConfig: () =>
    adminCall<{ config: PlatformStripeConfigSummary }>(
      'momentpix.admin.v1.PlatformBillingService',
      'GetPlatformStripeConfig',
      {},
    ),
  configureStripe: (req: {
    mode: string;
    publishableKey: string;
    secretKey: string;
    webhookSecret: string;
    statementDescriptor?: string;
  }) =>
    adminCall<{ config: PlatformStripeConfigSummary }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ConfigurePlatformStripe',
      req,
    ),
  verifyStripe: () =>
    adminCall<{ config: PlatformStripeConfigSummary; ok: boolean; error?: string }>(
      'momentpix.admin.v1.PlatformBillingService',
      'VerifyPlatformStripe',
      {},
    ),

  // Plans
  listPlans: (includeArchived = false) =>
    adminCall<{ plans: Plan[] }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ListPlans',
      { includeArchived },
    ),
  getPlan: (id: string) =>
    adminCall<{ plan: Plan }>('momentpix.admin.v1.PlatformBillingService', 'GetPlan', { id }),
  createPlan: (input: CreatePlanInput) =>
    adminCall<{ plan: Plan }>('momentpix.admin.v1.PlatformBillingService', 'CreatePlan', input),
  activatePlan: (id: string) =>
    adminCall<{ plan: Plan }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ActivatePlan',
      { id },
    ),
  archivePlan: (id: string) =>
    adminCall<{ plan: Plan }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ArchivePlan',
      { id },
    ),

  // Subscriptions
  getOrgSubscription: (orgId: string) =>
    adminCall<{ subscription?: Subscription }>(
      'momentpix.admin.v1.PlatformBillingService',
      'GetOrgSubscription',
      { orgId },
    ),
  assignPlanToOrg: (req: {
    orgId: string;
    planId: string;
    complimentary?: boolean;
    complimentaryReason?: string;
    reason: string;
  }) =>
    adminCall<{ subscription: Subscription }>(
      'momentpix.admin.v1.PlatformBillingService',
      'AssignPlanToOrg',
      req,
    ),
  setComplimentary: (req: { orgId: string; complimentary: boolean; reason: string }) =>
    adminCall<{ subscription: Subscription }>(
      'momentpix.admin.v1.PlatformBillingService',
      'SetComplimentary',
      req,
    ),

  // Invoices
  listInvoicesForOrg: (orgId: string, limit = 50) =>
    adminCall<{ invoices: Invoice[] }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ListInvoicesForOrg',
      { orgId, limit },
    ),
  getInvoice: (id: string) =>
    adminCall<{ invoice: Invoice }>(
      'momentpix.admin.v1.PlatformBillingService',
      'GetInvoice',
      { id },
    ),
  closePeriod: (orgId: string, reason: string) =>
    adminCall<{ invoice: Invoice; ledgerRowsRolled: number; creditApplied: boolean }>(
      'momentpix.admin.v1.PlatformBillingService',
      'ClosePeriod',
      { orgId, reason },
    ),
  renderInvoicePDF: (id: string) =>
    adminCall<{ invoice: Invoice; downloadUrl: string }>(
      'momentpix.admin.v1.PlatformBillingService',
      'RenderInvoicePDF',
      { id },
    ),
  issueCredit: (req: {
    orgId: string;
    amountCents: number;
    currency?: string;
    reason: string;
    notes?: string;
    applyToInvoiceId?: string;
  }) =>
    adminCall<{ ok: boolean }>(
      'momentpix.admin.v1.PlatformBillingService',
      'IssueCredit',
      req,
    ),

  // ── Phase 3c-UI operational actions ──
  retryCharge: (id: string, reason: string) =>
    adminCall<{
      invoice: Invoice;
      platformStripeInvoiceId: string;
      chargeStatus: string;
      error?: string;
    }>('momentpix.admin.v1.PlatformBillingService', 'RetryCharge', { id, reason }),
  voidInvoice: (id: string, reason: string) =>
    adminCall<{ invoice: Invoice }>(
      'momentpix.admin.v1.PlatformBillingService',
      'VoidInvoice',
      { id, reason },
    ),
  refundInvoice: (id: string, amountCents: number, reason: string) =>
    adminCall<{ invoice: Invoice; refundId: string }>(
      'momentpix.admin.v1.PlatformBillingService',
      'RefundInvoice',
      { id, amountCents, reason },
    ),
};

// ─── helpers ───

export function formatMoney(cents: number | string | undefined, currency: string = 'USD'): string {
  const n = typeof cents === 'string' ? Number(cents) : cents || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const dollars = Math.floor(abs / 100);
  const rem = abs % 100;
  const sym = currency === 'USD' ? '$' : currency + ' ';
  return `${sign}${sym}${dollars.toLocaleString()}.${String(rem).padStart(2, '0')}`;
}

export function bpsToPercent(bps: number): string {
  const n = num(bps);
  return `${(n / 100).toFixed(n % 100 === 0 ? 0 : 2)}%`;
}
